import { createContext, use, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { SsoApiError, getMe, type Me } from '@/modules/sso-api';
import { ssoKeys } from '@/modules/sso-query-keys';
import useAuthuser from '@/shared/utils/useAuthuser';

type Session = {
  me: Me;
  // The account the URL pins (?authuser=N); undefined = the browser default.
  authuser: number | undefined;
};

const SessionContext = createContext<Session | null>(null);

// The signed-in account for everything under RequireSession — the subtree
// renders only once the profile is known, so pages never write logged-out
// branches.
export function useSession(): Session {
  const session = use(SessionContext);
  if (!session) throw new Error('useSession must be used under RequireSession');
  return session;
}

export default function RequireSession({
  requireSetup = true,
}: {
  requireSetup?: boolean;
}) {
  const authuser = useAuthuser();
  const location = useLocation();
  const navigate = useNavigate();
  const me = useQuery({
    queryKey: ssoKeys.me(authuser),
    queryFn: () => getMe({ authuser }),
    retry: false,
  });

  const signedOut =
    me.isError && me.error instanceof SsoApiError && me.error.status === 401;
  const unfinished = requireSetup && me.isSuccess && me.data.setupRequired;
  // One navigation per bounce, from the effect: the router's <Navigate>
  // re-navigates on every re-render while the redirect is still pending.
  // The bounce carries the current URL as returnTo.
  const here = `${location.pathname}${location.searchStr}`;
  useEffect(() => {
    if (signedOut) {
      void navigate({
        to: '/login',
        search: { returnTo: here },
        replace: true,
      });
    } else if (unfinished) {
      void navigate({
        to: '/profile/setup',
        search: { returnTo: here },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per bounce, not per path change mid-redirect
  }, [signedOut, unfinished]);

  if (me.isPending || signedOut || unfinished) return null;
  if (me.isError) throw me.error;
  return <SessionProvider me={me.data} authuser={authuser} />;
}

function SessionProvider({ me: profile, authuser }: Session) {
  const session = useMemo(
    () => ({ me: profile, authuser }),
    [profile, authuser],
  );
  return (
    <SessionContext value={session}>
      <Outlet />
    </SessionContext>
  );
}
